import type { TElement, TText } from "platejs";
import type { AssemblyProject } from "../../../common/contexts/SelectionContext";
import {
  BLOCK_CHECKLIST,
  BLOCK_LOG,
  BLOCK_NOTA,
  BLOCK_OTF,
  BLOCK_PLECA,
  BLOCK_TRANSCRIPT,
  type ChecklistBlockElement,
} from "./schema";
import { formatDateLabel, pickDateSource } from "./blocks/dateLabel";

const SEP = "-".repeat(64);
const HEAD = "=".repeat(64);

/**
 * Resuelve los marcadores Stream/Ubicación de un bloque para el header del
 * export. Recibe el node entero porque la info vive en dos campos distintos:
 *   - `node.cameras[]` → chips de STREAM del logger (tabla `cameras`).
 *   - `node.tags[]`    → tags filtrados por categoría de ubicación.
 * La fuente de verdad del filtro vive en CortesEditor/index.tsx#resolveStreamTags
 * (sincronizá con LogCard.isStreamOrLocationTag si tocás cualquiera).
 */
export type StreamTagResolver = (node: any) => string[];

function nodeText(node: TElement): string {
  return ((node.children ?? []) as TText[])
    .map((c) => (typeof c?.text === "string" ? c.text : ""))
    .join("")
    .trim();
}

// Para bloques OTF promovidos por OTF MODE, mostramos "[OTF←LOG]" o
// "[OTF←TRANS]" para que el editor pueda rastrear el origen sin abrir el
// proyecto. originType nunca aparece en bloques nacidos como OTF en origen.
function otfLabelWithOrigin(originType: unknown): string {
  if (originType === "log") return "OTF←LOG";
  if (originType === "transcript" || originType === "transcription") return "OTF←TRANS";
  return "OTF";
}

function headerLine(label: string | null, el: any): string {
  const date = formatDateLabel(pickDateSource(el));
  const speaker = el.speakerName ? String(el.speakerName) : "";
  // LOG/TRANSCRIPT pasan label=null para que se vean igual y colapsen juntos
  // (ver groupKey). OTF sí muestra label porque marca el origen de la
  // promoción ([OTF←LOG] / [OTF←TRANS]).
  const labelPart = label ? `[${label}]` : "";
  const right = [labelPart, speaker].filter(Boolean).join(" ");
  if (!right) return date;
  return date ? `${date} — ${right}` : right;
}

function bodyWithTc(el: any, text: string): string {
  const tc = el.tc ? String(el.tc).trim() : "";
  if (!tc) return text;
  if (!text) return tc;
  return `${tc}  ${text}`;
}

function tagLine(node: any, resolver?: StreamTagResolver): string | null {
  if (!resolver) return null;
  const labels = resolver(node);
  if (!labels.length) return null;
  return `  · ${labels.join(" · ")}`;
}

// Identifica los tipos que se pueden colapsar con su antecesor cuando
// comparten fecha + speaker (cabecera repetida → ruido visual en el export).
function isGroupableType(t: unknown): boolean {
  return t === BLOCK_LOG || t === BLOCK_OTF || t === BLOCK_TRANSCRIPT;
}

function groupKey(el: any): string {
  const date = formatDateLabel(pickDateSource(el));
  const speaker = el.speakerName ? String(el.speakerName) : "";
  if (el.type === BLOCK_OTF) {
    // OTF mantiene label y origen en la cabecera, así que dos OTF contiguos
    // con orígenes distintos NO deben colapsar ([OTF←LOG] vs [OTF←TRANS]).
    const origin = el.originType ? String(el.originType) : "";
    return `otf|${date}|${speaker}|${origin}`;
  }
  // LOG y TRANSCRIPT ahora se exportan sin label, por lo que su cabecera es
  // idéntica y deben colapsar como un solo grupo "cuerpo".
  return `body|${date}|${speaker}`;
}

function blockToTxt(
  node: TElement,
  resolver?: StreamTagResolver,
  groupedWithPrev = false,
): string | null {
  const text = nodeText(node);
  const tags = tagLine(node, resolver);
  const withTags = (header: string, body: string) => {
    // Si la cabecera se colapsa con la anterior, sólo emitimos tags + body
    // (los tags pueden variar entre bloques agrupados, así que se quedan).
    if (groupedWithPrev) return tags ? `${tags}\n${body}` : body;
    return tags ? `${header}\n${tags}\n${body}` : `${header}\n${body}`;
  };

  switch (node.type) {
    case BLOCK_LOG:
      return withTags(headerLine(null, node), bodyWithTc(node, text));
    case BLOCK_OTF:
      return withTags(
        headerLine(otfLabelWithOrigin((node as any).originType), node),
        bodyWithTc(node, text),
      );
    case BLOCK_TRANSCRIPT:
      return withTags(headerLine(null, node), bodyWithTc(node, text));
    case BLOCK_PLECA:
      return `[PLECA]\n${text}`;
    case BLOCK_NOTA:
      // Recursos vacíos no aportan nada al export.
      return text ? `[RECURSO]\n${text}` : null;
    case BLOCK_CHECKLIST: {
      const c = node as ChecklistBlockElement;
      const lines = c.items.map((i) => `  [${i.done ? "x" : " "}] ${i.label}`);
      return `[CHECKLIST DE CIERRE]\n${lines.join("\n")}`;
    }
    case "h2":
      return text ? `## ${text.toUpperCase()}` : null;
    case "p":
    default:
      return text || null;
  }
}

export function buildExportTxt(
  project: AssemblyProject,
  titulo: string,
  sinopsis: string,
  doc: TElement[],
  autor: string,
  resolveStreamTags?: StreamTagResolver,
): string {
  const fecha = project.createdAt ? new Date(project.createdAt) : new Date();
  const fechaStr = fecha.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
  });

  const headerLines = [
    HEAD,
    (titulo || "Sin título").toUpperCase(),
    HEAD,
    `Fecha: ${fechaStr}`,
    `Autor: ${autor || "—"}`,
  ];
  if (sinopsis?.trim()) {
    headerLines.push("", "Sinopsis:", sinopsis.trim());
  }

  // Recorrido con `lastEmitted` (no el bloque previo crudo): un bloque que se
  // descarta — p.ej. nota vacía — no debe romper la agrupación entre los que
  // sí se emitieron a su alrededor.
  const parts: string[] = [];
  let lastEmitted: TElement | null = null;
  for (const n of doc) {
    const grouped =
      !!lastEmitted &&
      isGroupableType(n.type) &&
      isGroupableType(lastEmitted.type) &&
      groupKey(n) === groupKey(lastEmitted);
    const out = blockToTxt(n, resolveStreamTags, grouped);
    if (!out || !out.trim()) continue;
    if (parts.length > 0) parts.push(grouped ? "\n\n" : `\n\n${SEP}\n\n`);
    parts.push(out);
    lastEmitted = n;
  }

  return `${headerLines.join("\n")}\n\n${SEP}\n\n${parts.join("")}\n`;
}

export function downloadTxt(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Liberar el objeto URL en el próximo tick — Safari aborta la descarga si lo
  // revocás sincrónicamente.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function sanitizeFilename(name: string): string {
  return (name || "corte").replace(/[\\/:*?"<>|]+/g, "_").trim() || "corte";
}
